import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Save, Percent, Banknote, CreditCard, Globe } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Card, PageHeader, Spinner } from "../../components/dashboard/DashboardShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { rupiah } from "../../lib/format";

export default function AdminSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-settings"], queryFn: () => api.get("/admin/settings").then((r) => r.data) });
  const { data: pay } = useQuery({ queryKey: ["payment-config"], queryFn: () => api.get("/payments/config").then((r) => r.data) });
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data && !f) setF({ platform_fee_percent: data.platform_fee_percent, min_payout: data.min_payout }); }, [data, f]);
  if (isLoading || !f) return <Spinner />;
  const save = async () => {
    setBusy(true);
    try { const { data: d } = await api.put("/admin/settings", { platform_fee_percent: Number(f.platform_fee_percent), min_payout: Number(f.min_payout) }); qc.setQueryData(["admin-settings"], d); toast.success("Pengaturan disimpan"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHeader title="Pengaturan Platform" subtitle="Atur komisi, batas pencairan, dan integrasi." />
      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Komisi & Pencairan">
          <div className="space-y-4" data-testid="settings-form">
            <div>
              <label className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><Percent size={13} /> Komisi platform (% dari harga inspeksi)</label>
              <Input type="number" min={0} max={50} value={f.platform_fee_percent} onChange={(e) => setF({ ...f, platform_fee_percent: e.target.value })} className="mt-1.5" data-testid="settings-fee" />
              <p className="text-[11px] text-slate-400 mt-1">Contoh: inspeksi Rp 350.000 → komisi {rupiah(350000 * (Number(f.platform_fee_percent) || 0) / 100)}, inspector menerima {rupiah(350000 - 350000 * (Number(f.platform_fee_percent) || 0) / 100)}.</p>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><Banknote size={13} /> Minimum pencairan dana (Rp)</label>
              <Input type="number" min={0} value={f.min_payout} onChange={(e) => setF({ ...f, min_payout: e.target.value })} className="mt-1.5" data-testid="settings-min-payout" />
            </div>
            <Button onClick={save} disabled={busy} data-testid="settings-save-btn" className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl h-10 px-5 text-sm">{busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />} Simpan</Button>
          </div>
        </Card>
        <Card title="Integrasi Pembayaran">
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><CreditCard size={18} /></span>
            <div>
              <p className="text-sm font-extrabold text-slate-900">Midtrans Snap</p>
              <span className={`inline-block mt-1 text-[11px] font-bold rounded-full px-2.5 py-1 ${pay?.mock ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`} data-testid="midtrans-mode">{pay?.mock ? "Mode Simulasi (kunci belum diisi)" : "Terhubung (Sandbox/Production)"}</span>
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">{pay?.mock ? "Isi MIDTRANS_SERVER_KEY dan MIDTRANS_CLIENT_KEY di backend/.env lalu restart backend untuk mengaktifkan pembayaran asli." : `Client key: ${pay?.client_key?.slice(0, 18)}...`}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 mt-5 pt-5 border-t border-slate-100">
            <span className="w-10 h-10 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center"><Globe size={18} /></span>
            <div><p className="text-sm font-extrabold text-slate-900">Login Google</p><p className="text-xs text-slate-500 mt-1">Autentikasi menggunakan Google OAuth. Email admin dikonfigurasi lewat ADMIN_EMAILS di backend/.env.</p></div>
          </div>
        </Card>
      </div>
    </div>
  );
}
